import { useTranslation } from 'react-i18next';

import { LocaleLink } from '../../components/navigation/LocaleLink';
import { useCurrentLocale } from '../../lib/locale';
import { usePosts } from '../../lib/queries';
import type { Post } from '../../lib/types';
import { formatPostDate } from '../posts/postDate';
import { Carousel, CarouselItem } from './Carousel';

/**
 * Highlights — spec §7.1. A carousel of the latest News posts (§6.10 "Home Highlights carousel
 * pulls latest posts"). Renders nothing until posts exist, so the Home page degrades cleanly.
 */
export function Highlights() {
  const { t } = useTranslation();
  const { data: posts = [], isLoading } = usePosts('news', 9);

  if (isLoading || posts.length === 0) return null;

  return (
    <Carousel title={t('home.highlightsTitle')} subtitle={t('home.highlightsSubtitle')} titleClassName="text-accent">
      {posts.map((post) => (
        <CarouselItem key={post.id}>
          <HighlightCard post={post} />
        </CarouselItem>
      ))}
    </Carousel>
  );
}

function HighlightCard({ post }: { post: Post }) {
  const { t } = useTranslation();
  const locale = useCurrentLocale();

  return (
    <LocaleLink
      to={`/destination-management/news/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-panel shadow-soft transition-transform hover:-translate-y-1 dark:shadow-soft-dark"
    >
      <div className="aspect-[16/10] bg-content/5">
        {post.image && <img src={post.image} alt={post.title} className="h-full w-full object-cover" />}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-content-muted">
          {formatPostDate(post.published_at, locale)}
        </p>
        <h3 className="font-semibold leading-snug text-content">{post.title}</h3>
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="rounded-pill bg-content/5 px-3 py-1 text-xs font-medium text-content-muted">
            {t('posts.news')}
          </span>
          <span className="rounded-pill bg-ink px-4 py-1.5 text-xs font-semibold text-white">
            {t('posts.learnMore')}
          </span>
        </div>
      </div>
    </LocaleLink>
  );
}
